// RollupPanel.js — renders the der_rollup pass output (movie.rollups[grain]).
// Per grain: a plain SNAPSHOT (Working / Limiting / One move), a human SUMMARY
// paragraph (with the script-recognizing receipt), then the recurring-pattern
// cards (Reader effect / Where you already solve it / Revision move), a Protect
// card, and collapsed "other patterns". First-contact friendly — see
// der_rollup_views_spec_v0_1.md §9.

import React, { useState, useEffect, useRef, useContext, createContext } from 'react';
import PropTypes from 'prop-types';
import { grainTitle, grainPlural, grainSingular } from './grainNouns';
import { unitChipLabel } from './unitLabels';
import { tip } from './Tip';
import PageToc, { useScrollSpy, scrollToSection } from './PageToc';

const NAVY = '#14536e';
const ROSE = '#b3261e';
// neutral: the landing-page snapshot (PatternsModal). No count-derived red/amber/green or 'needs work'
// label — those are a recurrence count, not the Triage reading — and navy instead of rose accents
// (Codex rvw_e4d6859603373c1802c50215 HOME_patterns_severity; owner: red reads as an error there).
const LookCtx = createContext({ neutral: false });
const GREEN = '#2AA147';
const AMBER = '#9a6700';

// Keys and order are structure and stay here; the nouns come from the format (grainNouns.js).
// 'overview' is a tab rather than a grain, so it keeps its literal.
const GRAIN_KEYS = ['overview', 'scene', 'sequence', 'act'];
// `script` here means the WHOLE-SCRIPT rollup row, not the movie object — it kept its own two
// words ('Whole script' / 'units') and still does, because neither is a grain noun.
const tabLabel  = (s, k) => (k === 'overview' ? 'Overview' : grainTitle(s, k, k));
const unitTitle = (s, k) => (k === 'script' ? 'Whole script' : grainTitle(s, k, k));
const unitWordOf = (s, k) => (k === 'script' ? 'units' : grainPlural(s, k, 'units'));

export function GrainTabs({ grain, available, onChange, script }) {
  // `available` is the list of grain keys this script actually HAS (e.g. ['overview','sequence','scene']),
  // built from the timeline's real structure. Overview always shows; a level tab renders only if the script
  // has that level — so TV episodes / shorts with no act structure never get an Acts tab. (Also tolerates
  // the legacy object form {scene:true, act:false, …}.)
  const isPresent = (k) => {
    if (k === 'overview') return true;
    if (!available) return true;                       // no signal → show everything (back-compat)
    if (Array.isArray(available)) return available.includes(k);
    return available[k] !== false;
  };
  const shown = GRAIN_KEYS.filter(k => isPresent(k));
  return (
    <div className="d-flex gap-2 flex-wrap" role="tablist" aria-label="Rollup grain">
      {shown.map(g => {
        const active = g === grain;
        return (
          <button
            key={g} type="button" role="tab" aria-selected={active}
            onClick={() => onChange(g)}
            className="btn btn-sm"
            style={{
              fontWeight: 600, borderRadius: 999, padding: '.35rem 1rem',
              border: `1.5px solid ${active ? NAVY : '#cbd5e1'}`,
              background: active ? NAVY : '#fff',
              color: active ? '#fff' : NAVY,
              boxShadow: active ? '0 1px 4px rgba(20,83,110,.25)' : 'none',
            }}
          >
            {tabLabel(script, g)}
          </button>
        );
      })}
    </div>
  );
}
GrainTabs.propTypes = { grain: PropTypes.string, available: PropTypes.oneOfType([PropTypes.array, PropTypes.object]), onChange: PropTypes.func, script: PropTypes.object };

const FieldLabel = ({ children, color }) => (
  <span style={{ color: color || NAVY, fontWeight: 600, fontSize: '.88rem' }}>{children}</span>
);
FieldLabel.propTypes = { children: PropTypes.node, color: PropTypes.string };

// One recurring-pattern card (collapsible).
function HabitCard({ habit, open, onToggle, grain, onShowUnits, onSelectUnit, script, anchorId }) {
  const { neutral } = useContext(LookCtx); const acc = neutral ? NAVY : ROSE;
  const ev = habit.evidence || {};
  const count = ev.count;
  const unitWord = unitWordOf(script, grain);
  // ⚠️ was `unitWord.replace(/s$/,'')` — de-pluralising by chopping an 's' is a guess about English
  // that happens to work for scenes/acts/units. grainNouns carries the real singular; the chop
  // stays only as the fallback for a grain key that has no noun.
  const singular = grainSingular(script, grain, unitWord.replace(/s$/, ''));
  const [basisOpen, setBasisOpen] = useState(false);
  return (
    <div id={anchorId} className="mb-2" data-lr-habit-open={open ? '1' : undefined} style={{ border: `1.5px solid ${open ? acc + '55' : NAVY + '22'}`, borderLeft: `4px solid ${acc}`, borderRadius: 10, background: '#fff', scrollMarginTop: 80 }}>
      <button type="button" onClick={onToggle}
        className="w-100 text-start d-flex align-items-start gap-2 p-3"
        style={{ border: 'none', background: 'transparent', cursor: 'pointer', borderRadius: 10 }}>
        <span style={{ color: NAVY, fontWeight: 700, marginTop: 1 }}>{open ? '▾' : '▸'}</span>
        <span style={{ flex: 1 }}>
          <span style={{ fontWeight: 700, color: '#0f172a', lineHeight: 1.35 }}>{habit.name}</span>
          {count != null && (
            <span style={{ display: 'block', color: '#64748b', fontSize: '.82rem', marginTop: '.2rem' }}>
              Shows up in {count} {unitWord}{ev.cluster ? ` · ${ev.cluster}` : ''}{habit.confidence === 'low' ? ' · draft-wide tendency' : ''}
            </span>
          )}
          {open && Array.isArray(ev.units) && ev.units.length > 0 && (
            // ties this card to the outlined units on the timeline above (and back: the timeline chip scrolls here)
            <span role="link" tabIndex={0}
              onClick={e => { e.stopPropagation(); const c = document.getElementById('ic-pin-chip'); if (c) c.scrollIntoView({ behavior: 'smooth', block: 'center' }); }}
              onKeyDown={e => { if (e.key === 'Enter') { e.stopPropagation(); const c = document.getElementById('ic-pin-chip'); if (c) c.scrollIntoView({ behavior: 'smooth', block: 'center' }); } }}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: '.3rem', fontSize: '.78rem', fontWeight: 700, color: acc,
                       background: `${acc}0d`, border: `1px solid ${acc}44`, borderRadius: 999, padding: '.05rem .55rem', cursor: 'pointer' }}>
              <span style={{ width: 9, height: 9, border: `2px solid ${acc}`, borderRadius: 2, display: 'inline-block' }} />
              Outlined on the timeline ↑
            </span>
          )}
        </span>
      </button>
      {open && (
        <div className="px-3 pb-3" style={{ marginLeft: 22 }}>
          {habit.consequence && (
            <div style={{ marginBottom: '.5rem' }}>
              <FieldLabel>Reader effect</FieldLabel>
              <div style={{ color: '#334155', lineHeight: 1.45, fontSize: '.9rem' }}>{habit.consequence}</div>
            </div>
          )}
          {habit.calibration && habit.calibration.text && (
            <div style={{ marginBottom: '.5rem' }}>
              <FieldLabel color={GREEN}>{habit.calibration.form === 'absence_note' ? 'Calibration' : 'Where you already solve it'}</FieldLabel>
              <div style={{ color: '#334155', lineHeight: 1.45, fontSize: '.9rem' }}>{habit.calibration.text}</div>
            </div>
          )}
          {habit.leverage && (
            <div style={{ marginBottom: '.4rem' }}>
              <FieldLabel>Revision move</FieldLabel>
              <div style={{ color: '#0f172a', lineHeight: 1.45, fontSize: '.9rem' }}>{habit.leverage}</div>
            </div>
          )}
          {Array.isArray(ev.units) && ev.units.length > 0 && (
            <div>
              <FieldLabel>Where it shows up</FieldLabel>
              <div className="d-flex flex-wrap align-items-center gap-1" style={{ marginTop: '.25rem' }}>
	                {ev.units.slice(0, 5).map((u, k) => (
	                  onSelectUnit ? (
	                    <button key={k} type="button" onClick={() => onSelectUnit(u)}
	                      {...tip(`Highlight ${unitChipLabel(u)} on the timeline`)}
	                      style={{
	                        fontSize: '.82rem', color: NAVY, background: `${NAVY}0d`, cursor: 'pointer',
	                        border: `1px solid ${NAVY}33`, borderRadius: 999, padding: '.1rem .6rem',
	                      }}>{unitChipLabel(u)}</button>
	                  ) : (
	                    <span key={k} style={{
	                      fontSize: '.82rem', color: NAVY, background: `${NAVY}0d`,
	                      border: `1px solid ${NAVY}22`, borderRadius: 999, padding: '.1rem .55rem',
	                    }}>{unitChipLabel(u)}</span>
	                  )
	                ))}
                {ev.units.length > 5 && (
                  <span style={{ color: '#64748b', fontSize: '.82rem' }}>+{ev.units.length - 5} more</span>
                )}
                {onShowUnits && ev.units.length > 1 && (
                  <button type="button" onClick={() => onShowUnits(ev.units, habit.name)}
                    className="btn btn-sm btn-link text-decoration-none px-0 ms-1" style={{ color: NAVY, fontWeight: 600, fontSize: '.84rem' }}>
                    light all {ev.units.length} on the timeline →
                  </button>
                )}
              </div>
            </div>
          )}

          {/* how we got this — the count and the flagged units come from a fixed rule over the stored scores;
              the scores are still AI judgments (Codex HOME_patterns_determinism: never call them deterministic). */}
          <div style={{ marginTop: '.65rem', borderTop: '1px dashed #e6edf2', paddingTop: '.5rem' }}>
            <button type="button" onClick={() => setBasisOpen(o => !o)}
              style={{ border: 'none', background: 'transparent', color: NAVY, fontWeight: 600, fontSize: '.82rem', cursor: 'pointer', padding: 0 }}>
              {basisOpen ? '▾' : '▸'} how we got this
            </button>
            {basisOpen && (
              <div style={{ marginTop: '.45rem', fontSize: '.84rem', color: '#334155', lineHeight: 1.5, background: '#f8fafc', border: '1px solid #eef2f6', borderRadius: 8, padding: '.55rem .7rem' }}>
                <div style={{ marginBottom: '.35rem' }}>
                  {habit.derivation
                    ? habit.derivation
                    : (<>Every {singular} is scored on 12 axes by the same frozen grader, then this pattern is flagged by a fixed rule:{' '}
                        {count != null
                          ? <><b>{count}</b> {unitWord} scored as a miss on this axis where it was owed{ev.cluster ? <>, concentrated <b>{ev.cluster}</b></> : null}.</>
                          : <>the owed {unitWord} that under-delivered on this axis{ev.cluster ? <>, concentrated <b>{ev.cluster}</b></> : null}.</>}
                        {Array.isArray(ev.units) && ev.units.length > 0 ? <> The flagged {unitWord} are the chips above — click any to light it on the timeline.</> : null}</>)}
                </div>
                <div style={{ color: '#94a3b8', fontSize: '.8rem' }}>
                  The count follows the stored scores by a fixed rule. The scores themselves, the description and the suggested move are AI readings for you to judge.
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
HabitCard.propTypes = { anchorId: PropTypes.string, habit: PropTypes.object, open: PropTypes.bool, onToggle: PropTypes.func, grain: PropTypes.string, onShowUnits: PropTypes.func, onSelectUnit: PropTypes.func, script: PropTypes.object };

function GrainRollupView({ data, grain, onShowUnits, onSelectUnit, script, focus }) {
  const habits = data.habits || [];
  const snap = data.snapshot || null;
  const other = data.other_signals || [];
  // `focus` is the deep-link request (deepLink.js: `lr=patterns:scene:2` / `:other`). It only seeds the
  // INITIAL state — once the reader clicks, their choice wins.
  // All levels now sit on ONE page, so cards start COLLAPSED (the level summary is already showing);
  // a deep link to a specific pattern opens that one.
  const [openIdx, setOpenIdx] = useState(() => {
    const h = focus && focus.habit ? focus.habit - 1 : -1;
    return h >= 0 && h < habits.length ? h : -1;
  });
  const [otherOpen, setOtherOpen] = useState(() => !!(focus && focus.other && other.length));
  // A LATER request (history, a deep link while Patterns is already open) opens its target too, not just
  // the first one at mount (Codex G_pattern_focus_update). Reader toggles between requests are kept.
  useEffect(() => {
    if (!focus) return;
    if (focus.habit && focus.habit - 1 < habits.length) setOpenIdx(focus.habit - 1);
    if (focus.other && other.length) setOtherOpen(true);
  }, [focus]);   // eslint-disable-line react-hooks/exhaustive-deps
  const otherRef = useRef(null);
  useEffect(() => {
    if (focus && focus.other && otherRef.current && otherRef.current.scrollIntoView) {
      otherRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  // Light the OPENED pattern's footprint on the timeline. Only when a card is opened (a reader's action,
  // or a deep link) — with every level on one page, lighting on mount would have three levels fighting
  // over the map. Diffuse patterns with no units simply don't light — no fake ranges.
  useEffect(() => {
    if (openIdx < 0) return;
    const u = (habits[openIdx] && habits[openIdx].evidence && habits[openIdx].evidence.units) || [];
    if (u.length && onShowUnits) onShowUnits(u, habits[openIdx].name);   // the NAME goes with it, so the timeline can say which pattern it shows
  }, [openIdx]);   // eslint-disable-line react-hooks/exhaustive-deps

  if (habits.length === 0 && !snap && !data.summary) {
    return <div className="text-muted">No strong recurring pattern at the {grainSingular(script, grain, 'unit')} level — the work is more unit-by-unit here.</div>;
  }

  return (
    <>
      {data.summary && (
        <div style={{ marginBottom: '1rem' }}>
          <div style={{ color: NAVY, fontWeight: 600, fontSize: '1rem', marginBottom: '.3rem' }}>
            The pattern across {unitWordOf(script, grain)}
          </div>
          <div style={{ color: '#334155', lineHeight: 1.55, fontSize: '1rem' }}>{data.summary}</div>
        </div>
      )}

      {habits.map((h, i) => (
        <HabitCard key={i} anchorId={`lr-pat-${grain}-h${i}`} habit={h} grain={grain}
          open={openIdx === i} onToggle={() => setOpenIdx(openIdx === i ? -1 : i)} onShowUnits={onShowUnits} onSelectUnit={onSelectUnit} script={script} />
      ))}

      {data.protect && data.protect.name && (
        <div id={`lr-pat-${grain}-protect`} className="p-3 mb-2" style={{ border: `1.5px solid ${GREEN}33`, borderLeft: `4px solid ${GREEN}`, borderRadius: 10, background: '#f6fbf7', scrollMarginTop: 64 }}>
          <FieldLabel color={GREEN}>Protect this</FieldLabel>
          <div style={{ color: '#0f172a', fontWeight: 600, lineHeight: 1.4 }}>{data.protect.name}</div>
          {data.protect.detail && <div style={{ color: '#334155', fontSize: '.92rem', lineHeight: 1.5, marginTop: '.3rem' }}>{data.protect.detail}</div>}
          {data.protect.breaks_if && <div style={{ color: '#475569', fontSize: '.86rem', marginTop: '.35rem' }}>Breaks if: {data.protect.breaks_if}</div>}
        </div>
      )}

      {other.length > 0 && (
        <div className="mt-2" ref={otherRef} id={`lr-pat-${grain}-other`} style={{ scrollMarginTop: 64 }}>
          <button type="button" onClick={() => setOtherOpen(o => !o)}
            className="btn btn-sm btn-link text-decoration-none px-0" style={{ color: NAVY, fontWeight: 600 }}>
            {otherOpen ? '▾' : '▸'} One more, smaller pattern{other.length > 1 ? `s (${other.length})` : ''}
          </button>
          {otherOpen && other.map((o, i) => (
            <div key={i} className="p-2 mb-1" style={{ border: '1px solid #eef2f6', borderRadius: 8 }}>
              <div style={{ fontWeight: 600, color: '#0f172a', fontSize: '.9rem' }}>{o.name}</div>
              {o.consequence && <div style={{ color: '#64748b', fontSize: '.84rem' }}>{o.consequence}</div>}
              {o.leverage && <div style={{ color: '#334155', fontSize: '.86rem', marginTop: '.2rem' }}>{o.leverage}</div>}
            </div>
          ))}
        </div>
      )}
    </>
  );
}
GrainRollupView.propTypes = { data: PropTypes.object, grain: PropTypes.string, onShowUnits: PropTypes.func, onSelectUnit: PropTypes.func, script: PropTypes.object, focus: PropTypes.object };

// Severity proxy for a level → its dot and status word (more patterns, and a dragging snapshot, rank higher).
const levelSev = (r) => (r ? (r.habits || []).length * 2 + (r.snapshot && r.snapshot.dragging ? 1 : 0) : 0);
const levelDot = (r, neutral) => { if (neutral) return NAVY; const s = levelSev(r); return s >= 4 ? ROSE : (s >= 2 ? AMBER : GREEN); };
const levelWord = (r, neutral) => {
  if (neutral) { const n = (r && r.habits || []).length; return n ? `${n} recurring ${n === 1 ? 'pattern' : 'patterns'}` : ''; }
  const s = levelSev(r); return s >= 4 ? 'needs work' : (s >= 2 ? 'mixed' : 'holding');
};
const hasLevel = (r) => !!(r && (r.snapshot || (r.habits || []).length || r.summary));

// OVERVIEW = the map of the page (owner, 2026-10-14): each level in very simple terms — the pattern and
// the one move — and "See more" SCROLLS to that level's section further down. It never switches views
// and never expands in place, so the reader always knows where they are and how to get back (the TOC).
function OverviewView({ rollups, levels, script }) {
  const { neutral } = useContext(LookCtx);
  if (levels.length === 0) {
    return <div className="text-muted">No patterns yet. Run the rollup pass for this script.</div>;
  }
  const protectGrain = levels.find(g => rollups[g].protect && rollups[g].protect.name);
  const protect = protectGrain ? rollups[protectGrain].protect : null;
  return (
    <>
      {/* Titled "Overview" to match its entry in the TOC (owner, 2026-10-14), styled like the level headings. */}
      <h3 style={{ fontSize: '1.15rem', fontWeight: 700, color: '#0f172a', margin: '0 0 .25rem' }}>Overview</h3>
      <div style={{ color: '#64748b', fontSize: '.88rem', marginBottom: '.8rem' }}>
        What repeats at each level: one pattern and one move per level. <b>See more</b> takes you to that level below.
      </div>
      <div className="d-flex flex-column gap-2">
        {levels.map(g => {
          const r = rollups[g]; const snap = r.snapshot || {}; const h0 = (r.habits || [])[0] || {};
          const headline = snap.dragging || h0.name;
          const move = snap.one_move || h0.leverage;
          return (
            <div key={g} className="p-3" style={{ border: '1.5px solid #e2e8f0', borderRadius: 12, background: '#fff' }}>
              <div className="d-flex align-items-center justify-content-between" style={{ gap: 10 }}>
                <span className="d-flex align-items-center" style={{ gap: 8 }}>
                  <span style={{ width: 9, height: 9, borderRadius: 999, background: levelDot(r, neutral), display: 'inline-block' }} />
                  <span style={{ fontWeight: 700, color: '#0f172a' }}>{unitTitle(script, g)}</span>
                  <span style={{ color: '#64748b', fontSize: '.8rem' }}>{levelWord(r, neutral)}</span>
                </span>
                <button type="button" onClick={() => scrollToSection(`lr-pat-${g}`)}
                  style={{ border: 'none', background: 'none', color: NAVY, fontWeight: 700, fontSize: '.86rem', whiteSpace: 'nowrap', cursor: 'pointer', padding: 0 }}>
                  See more ↓
                </button>
              </div>
              {headline && <div style={{ color: '#334155', lineHeight: 1.45, marginTop: '.45rem' }}>{headline}</div>}
              {move && <div style={{ color: '#0f172a', fontWeight: 500, marginTop: '.35rem', fontSize: '.94rem' }}>→ {move}</div>}
            </div>
          );
        })}
      </div>
      {protect && (
        <div className="p-3 mt-3" style={{ border: `1.5px solid ${GREEN}33`, borderLeft: `4px solid ${GREEN}`, borderRadius: 10, background: '#f6fbf7' }}>
          <FieldLabel color={GREEN}>Protect</FieldLabel>
          <span style={{ color: '#0f172a', fontWeight: 600, marginLeft: 6 }}>{protect.name}</span>
        </div>
      )}
    </>
  );
}
OverviewView.propTypes = { rollups: PropTypes.object, levels: PropTypes.array, script: PropTypes.object };

// ONE PAGE, NOT TABS (owner, 2026-10-14). The old Overview / Scenes / Sequences / Acts tabs showed the
// same levels twice — as tabs and as cards — and clicking a card silently switched tabs. Now: the
// overview at the top, every level's full section below it, and an "On this page" TOC on the left
// (a chip strip on narrow panels) that shows where you are and jumps anywhere, however much is open.
// `grain` (from a row click or a deep link like lr=patterns:scene:2) now means "scroll to that level".
export default function RollupPanel({ rollups, grain, available, onShowUnits, onSelectUnit, script, focus, neutral = false }) {
  const R = rollups || {};
  const isPresent = (k) => {
    if (!available) return true;
    if (Array.isArray(available)) return available.includes(k);
    return available[k] !== false;
  };
  const levels = ['scene', 'sequence', 'act'].filter(g => isPresent(g) && hasLevel(R[g]));
  const rootRef = useRef(null);
  const [w, setW] = useState(0);
  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => setW(el.clientWidth)); ro.observe(el); setW(el.clientWidth);
    return () => ro.disconnect();
  }, []);
  const items = [{ id: 'lr-pat-overview', label: 'Overview' }];
  levels.forEach(g => {
    const r = R[g];
    items.push({ id: `lr-pat-${g}`, label: unitTitle(script, g), color: levelDot(r, neutral) });
    (r.habits || []).forEach((h, i) => items.push({ id: `lr-pat-${g}-h${i}`, label: h.name, full: h.name, indent: true, parent: `lr-pat-${g}` }));
    if (r.protect && r.protect.name) items.push({ id: `lr-pat-${g}-protect`, label: 'Protect this', indent: true, parent: `lr-pat-${g}` });
    if ((r.other_signals || []).length) items.push({ id: `lr-pat-${g}-other`, label: 'Smaller patterns', indent: true, parent: `lr-pat-${g}` });
  });
  const [active] = useScrollSpy(items.map(it => it.id), rootRef);
  useEffect(() => {
    if (grain && grain !== 'overview' && levels.includes(grain)) {
      const id = focus && focus.habit ? `lr-pat-${grain}-h${focus.habit - 1}` : (focus && focus.other ? `lr-pat-${grain}-other` : `lr-pat-${grain}`);
      requestAnimationFrame(() => scrollToSection(id));
    }
  }, [grain, focus]);   // eslint-disable-line react-hooks/exhaustive-deps
  const side = w >= 820;
  const section = { scrollMarginTop: side ? 16 : 64 };
  return (
    <LookCtx.Provider value={{ neutral }}>
    <div ref={rootRef} className="p-3">
      {!side && <PageToc layout="strip" items={items} activeId={active} />}
      <div style={{ display: side ? 'flex' : 'block', gap: 32, alignItems: 'flex-start' }}>
        {side && <div style={{ flex: '0 0 210px', position: 'sticky', top: 12, alignSelf: 'flex-start' }}><PageToc items={items} activeId={active} stickyTop={0} /></div>}
        <div style={{ flex: 1, minWidth: 0, maxWidth: 720 }}>
          <section id="lr-pat-overview" style={section}>
            <OverviewView rollups={R} levels={levels} script={script} />
          </section>
          {levels.map(g => (
            <section key={g} id={`lr-pat-${g}`} style={{ ...section, marginTop: '2.2rem', paddingTop: '1.2rem', borderTop: '1px solid #e2e8f0' }}>
              <h3 className="d-flex align-items-center" style={{ gap: 8, fontSize: '1.15rem', fontWeight: 700, color: '#0f172a', margin: '0 0 .7rem' }}>
                <span style={{ width: 10, height: 10, borderRadius: 999, background: levelDot(R[g], neutral), display: 'inline-block' }} />
                {unitTitle(script, g)}
                <span style={{ color: '#64748b', fontSize: '.82rem', fontWeight: 600 }}>{levelWord(R[g], neutral)}</span>
              </h3>
              <GrainRollupView data={R[g]} grain={g} onShowUnits={onShowUnits} onSelectUnit={onSelectUnit} script={script}
                focus={focus && grain === g ? focus : null} />
            </section>
          ))}
        </div>
      </div>
    </div>
    </LookCtx.Provider>
  );
}
RollupPanel.propTypes = {
  rollups: PropTypes.object,
  grain: PropTypes.string,
  available: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  onGrainChange: PropTypes.func,
  onShowUnits: PropTypes.func,
  onSelectUnit: PropTypes.func,
  script: PropTypes.object,
  focus: PropTypes.object,
  neutral: PropTypes.bool,
};
